import type { MediaUrlSource } from "./useMediaUrl";

/**
 * Merge a session's own media reference with the matching row from its media
 * relation, field by field.
 *
 * Every echo asset (final video, script) is described twice: the session
 * profile row carries a projected copy, and the session's media relation
 * carries the row the indexer wrote when the asset was created. Either can be
 * incomplete — projection onto the profile row is a separate write that can be
 * missed (three of 75 delivered echoes in the week to 2026-08-20 had no
 * `final_video_s3_key`), so the media row is the fallback that keeps the asset
 * reachable.
 *
 * The merge is per field rather than per source: a session that projected a
 * bucket but no key still resolves, because the key comes from the media row.
 * And "present" means a non-blank string — the bug this replaces used `??`,
 * which treats `""` as a value, so one empty field silently defeated the
 * fallback and the CRM reported a delivered echo as having no video.
 */
export function resolveMediaSource(
  primary: MediaUrlSource | null | undefined,
  fallback: MediaUrlSource | null | undefined,
): MediaUrlSource {
  return {
    url: present(primary?.url) ?? present(fallback?.url),
    storage_bucket:
      present(primary?.storage_bucket) ?? present(fallback?.storage_bucket),
    storage_path:
      present(primary?.storage_path) ?? present(fallback?.storage_path),
    storage_is_public:
      primary?.storage_is_public ?? fallback?.storage_is_public ?? false,
  };
}

function present(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}
